import { useState, useEffect, forwardRef, useImperativeHandle, useRef, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import isEqual from "lodash.isequal";

import { groupVariantOptions } from "../utils/groupVariantOptions";
import { showError } from "@/utils/toast";

import FormInput from "@/components/formInputFields/FormInput";
import FormCard from "@/components/formInputFields/FormCard";
import { Plus } from "lucide-react";
import { Trash2 } from "@/components/icons_new";
import VariantComboTable from "./VariantComboTable";

const VariantSection = forwardRef(({ isHydrating }, ref) => {
  // pull the values from the form
  const { getValues, setValue, formState, trigger } = useFormContext();

  // make a list of variant oprions
  const [options, setOptions] = useState([]);
  const [errors, setErrors] = useState({});

  const variantErrors = formState.errors?.variants || [];
  
  // hydrated local state from existing form values on first render
  useEffect(() => {
    const existing = getValues("variants");

    if (Array.isArray(existing) && existing.length > 0 && options.length === 0) {
      const grouped = groupVariantOptions(existing);
      
      setOptions(grouped);
    }
  }, []);


  // keep the form synced as user edits the dynamic fields
  useEffect(() => {
    const variantMap = options.reduce((acc, opt) => {
      const cleanedValues = opt.variantValues
        .map((val) => val.trim())
        .filter((val) => val !== "");
    
      if (cleanedValues.length > 0) {
        acc[opt.variantOption] = cleanedValues;
      }
    
      return acc;
    }, {});
  
    const cleaned = options.map(opt => ({
      variantOption: opt.variantOption.trim(),
      variantValues: opt.variantValues.map(val => val.trim())
    }));
  
    const prev = getValues("variants");
  
    if (Object.keys(variantMap).length === 0) {
      if (!isEqual(prev, [])) {
        setValue("variants", [], !isHydrating.current ? { shouldDirty: true } : {});
        if (!isHydrating.current) trigger("variants");
      }
      return;
    }
  
    if (!isEqual(prev, cleaned)) {
      setValue("variants", cleaned, !isHydrating.current ? { shouldDirty: true } : {});
      if (!isHydrating.current) trigger("variants");
    }
  }, [options, setValue, getValues]);

  // Reopen any variant option if there are Zod validation errors
  useEffect(() => {
    const variantErrors = formState.errors?.variants;
    if (Array.isArray(variantErrors)) {
      const updated = [...options];
      let changed = false;

      variantErrors.forEach((err, index) => {
        if (err && !updated[index]?.isEditing) {
          updated[index] = {
            ...updated[index],
            isEditing: true,
          };
          changed = true;
        }
      });

      if (changed) {
        setOptions(updated);
      }
    }
  }, [formState.errors?.variants]);
  
  // add a new variant option
  const handleAddOption = () => {
    if (options.length >= 2) {
      showError("You can only have up to 2 options.");
      return;
    }

    setOptions((prev) => [
      ...prev,
      { variantOption: "", variantValues: [""], isEditing: true },
    ]);
  };

  // Update the option name
  const handleOptionNameChange = (index, value) => {
    const updated = [...options];
    updated[index].variantOption = value;
    setOptions(updated);
  };

  // Update the specific value under an option
  const handleValueChange = (optIndex, valIndex, value) => {
    const updated = [...options];
    updated[optIndex].variantValues[valIndex] = value;
    setOptions(updated);
  };

  // Add another value field under an option
  const handleAddValue = (index) => {
    if (options[index].variantValues.length >= 10) {
      showError("Each option can only have up to 10 values.");
      return;
    }

    const updated = [...options];
    updated[index].variantValues.push("");
    setOptions(updated);
  };

  // Delete a specific value from an option
  const handleDeleteValue = (optIndex, valIndex) => {
    const updated = [...options];
    updated[optIndex].variantValues.splice(valIndex, 1);
    setOptions(updated);
  };

  // Delete an entire option
  const handleDeleteOption = (index) => {
    const updated = [...options];
    updated.splice(index, 1);
    setOptions(updated);
  };

  // Confirm and collapse the option into preview mode
  const handleDone = (index) => {
    const opt = options[index];

    // Trim and validate the option name
    const cleanedOptionName = opt.variantOption.trim();
    const isOptionValid = cleanedOptionName.length > 0;

    // Trim all values, remove empties and duplicates
    const cleanedValues = Array.from(
      new Set(opt.variantValues.map((val) => val.trim()).filter(Boolean))
    );

    const hasValidValues = cleanedValues.length > 0;

    if (!isOptionValid || !hasValidValues) {
      setErrors((prev) => ({
        ...prev,
        [index]: "Please enter a valid option name and at least one unique value.",
      }));
      return;
    }

    // Clear any orevious error
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });

    // Collapse the section if valid
    const updated = [...options];
    updated[index] = {
      ...updated[index],
      variantOption: cleanedOptionName,
      variantValues: cleanedValues,
      isEditing: false,
    };
    setOptions(updated);
  };

  // Check for any unfinished options (not yet collapsed with Done)
  const hasUnfinishedVariants = () => {
    return options.some((opt) => opt.isEditing);
  };

  useImperativeHandle(ref, () => ({
    hasUnfinishedVariants,
  }));

  // Reopen the option for editing by clicking
  const handleEditClick = (index) => {
    const updated = [...options];
    updated[index].isEditing = true;
    setOptions(updated);
  };

  // Show the variant combo table when there is a variant option and a value
  const hasLiveVariantMap = options.some(
    (opt) => opt.variantOption.trim() && opt.variantValues.some((val) => val.trim())
  );

  // Seed product pricing defaults only once when variants are first created
  const hasSeededPricing = useRef(false);
  useEffect(() => {
    if (hasLiveVariantMap && !hasSeededPricing.current) {
      hasSeededPricing.current = true;
    }
  }, [hasLiveVariantMap]);

  const productPricingDefaults = useMemo(() => {
    const toNumber = (v) => {
      const n = Number(v);
      return isFinite(n) ? n : undefined;
    };
    const price = toNumber(getValues("productPrice"));
    const discounted = toNumber(getValues("productDiscountedPrice"));
    const cost = toNumber(getValues("productCostPerItem"));
    return {
      // Variant price = discounted price when present, else product price
      price: discounted ?? price ?? "",
      // Variant compare-at = original product price
      compareAtPrice: price ?? "",
      costPerItem: cost ?? "",
    };
  }, [getValues]);

  return (
    <FormCard title="Variants" className="w-full max-w-3xl">
      {/* If no options, just show "Add options" button */}
      {options.length === 0 && (
        <button
          type="button"
          onClick={handleAddOption}
          className="text-sm text-gray-600 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add options like size or color
        </button>
      )}

      {/* Render each option */}
      {options.map((option, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-md p-4 mb-4"
        >
          {/* Editing state */}
          {option.isEditing ? (
            (() => {
              const trimmedOption = option.variantOption.trim();
              const cleanedValues = option.variantValues.map((val) => val.trim()).filter(Boolean);
              const canSubmit = trimmedOption.length > 0 && cleanedValues.length > 0;

              return (
                <>
                  {/* Product Variant Option */}
                  <FormInput
                    label="Option name"
                    value={option.variantOption}
                    onChange={(e) => handleOptionNameChange(index, e.target.value)}
                    placeholder="e.g. Size"
                    className="w-[50%]"
                    error={variantErrors?.[index]?.variantOption}
                  />

                  {/* Render the list of values for the option */}
                  {option.variantValues.map((val, valIndex) => (
                    <div
                      key={valIndex}
                      className="flex items-center w-fit h-fit gap-1 mt-2"
                    >
                      <FormInput
                        value={val}
                        onChange={(e) =>
                          handleValueChange(index, valIndex, e.target.value)
                        }
                        placeholder="e.g. Medium"
                        className="mb-0 !py-1.5 !h-auto !leading-none"
                        error={variantErrors?.[index]?.variantValues?.[valIndex]}
                      />

                      {option.variantValues.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleDeleteValue(index, valIndex)}
                          className="text-sm text-[#656565] h-fit flex flex-row gap-[0.3em] items-center rounded-sm py-1 px-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => handleAddValue(index)}
                    className="text-sm text-blue-500 mt-2"
                  >
                    + Add another value
                  </button>

                  {(errors[index]) && (
                    <p className="text-sm text-red-500 mt-2">
                      {errors[index]}
                    </p>
                  )}

                  <div className="flex gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => handleDeleteOption(index)}
                      className="text-sm text-red-600 border border-red-300 px-3 py-1 rounded-md"
                    >
                      Delete Option
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDone(index)}
                      disabled={!canSubmit}
                      className={`text-sm px-4 py-1 rounded-md ${
                        canSubmit
                          ? "bg-[#000] text-white hover:bg-gray-800"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      Done
                    </button>
                  </div>

                  {!canSubmit && (
                    <p className="text-xs text-gray-500 mt-1">
                      Enter a name and at least one value to continue.
                    </p>
                  )}
                </>
              );
            })()
          ) : (
            <div
              className="cursor-pointer"
              onClick={() => handleEditClick(index)}
            >
              <p className="font-medium text-sm mb-1">{option.variantOption}</p>
              <div className="flex gap-2 flex-wrap">
                {option.variantValues.map((val, i) => (
                  <span
                    key={i}
                    className="bg-gray-100 text-sm px-2 py-1 rounded"
                  >
                    {val}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {formState.errors?.variants?.message && (
        <p className="text-sm text-red-500 mt-2">
          {formState.errors.variants.message}
        </p>
      )}

      {/* options is currently internal state. */}
      {/* If we later pull it from backend or props, wrap with Array.isArray() */}
      {options.length > 0 && options.length < 2 && (
        <button
          type="button"
          onClick={handleAddOption}
          className="text-sm text-gray-600 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add another option
        </button>
      )}

      {hasLiveVariantMap && (
        <div className="">
          <VariantComboTable
            variantMap={Object.fromEntries(
              options
                .filter((opt) => opt.variantOption.trim() && opt.variantValues.some((val) => val.trim()))
                .map((opt) => [opt.variantOption.trim(), opt.variantValues.map((v) => v.trim())])
            )}
            productPricingDefaults={!hasSeededPricing.current ? productPricingDefaults : null}
          />
        </div>
      )}
    </FormCard>
  );
});

export default VariantSection;
